// Shared DOM pieces for game overlays (desktop + phone): the "Play X" prompt,
// the lobby (seat list, computer players, host, start), leave, rules guide,
// leaderboard button, turn timer and the game-over block. Spades uses them;
// a new game's Overlay composes them around its own board. Styles:
// kit/gameUi.css (gx-sp-* classes).

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useGames, tableAction, sitInChair, standFromChair, type TableEntry, type TableState } from '../gameStore';
import type { GameClientDef, OverlayProps } from '../types';
import { Leaderboard } from '../Leaderboard';
import './gameUi.css';

export const seatLabelOf = (def: GameClientDef, i: number) => def.seatNames?.[i] ?? `Seat ${i + 1}`;
export const seatedCount = (st: TableState) => st.seats.filter(Boolean).length;

/** Take a seat (lobby / mid-game join) or a computer player's seat, then walk into its chair (if it has one). */
export async function joinTable(pid: string, st: TableState, seat?: number) {
  const botSeat = st.seats.findIndex((s) => s?.kind === 'bot');
  const extra = seat !== undefined ? { seat } : st.phase === 'lobby' || botSeat < 0 ? {} : { seat: botSeat };
  const t = await tableAction(pid, 'sit', extra);
  const me = useGames.getState().me;
  const i = t ? t.state.seats.findIndex((s) => s?.kind === 'human' && s.userId === me) : -1;
  if (i >= 0) sitInChair(pid, i);
  return i;
}

/** Leave the seat (or stop hosting) and stand up. */
export function leaveTable(pid: string) {
  void tableAction(pid, 'leave');
  standFromChair();
}

/** What joining would do right now: a label for the button, or null if you can only watch. */
export function joinOption(def: GameClientDef, st: TableState): { label: string } | null {
  const open = st.seats.findIndex((s) => !s);
  const botSeat = st.seats.findIndex((s) => s?.kind === 'bot');
  if (st.phase === 'lobby') return open >= 0 ? { label: `Play ${def.label}` } : botSeat >= 0 ? { label: `Take over ${st.seats[botSeat]!.name}` } : null;
  if (st.phase === 'playing' && def.joinMidGame && open >= 0) return { label: 'Join in' };
  return botSeat >= 0 ? { label: `Take over ${st.seats[botSeat]!.name}` } : null;
}

/** "How to play X": the registry rules, plus the video link if any. */
export function RulesModal({ def, onClose }: { def: GameClientDef; onClose: () => void }) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  // Mounted on the viewer root (not inside the blurred panel, whose
  // backdrop-filter would trap position:fixed) so compact-HUD rules apply.
  const host = (document.querySelector('.gx-viewer') as HTMLElement | null) ?? document.body;
  return createPortal(
    <div className="gx-sp-rules-backdrop" onClick={onClose}>
      <div className="gx-sp-rules" role="dialog" aria-modal="true" aria-labelledby="gx-sp-rules-title" onClick={(e) => e.stopPropagation()}>
        <div className="gx-sp-rules-head">
          <h2 id="gx-sp-rules-title"><span className="gx-sp-spade">{def.glyph}</span> How to play {def.label}</h2>
          <button ref={closeRef} className="gx-sp-btn gx-sp-btn--ghost" onClick={onClose} aria-label="Close">Close</button>
        </div>
        <div className="gx-sp-rules-body">
          {(def.rules ?? []).map((sec) => (
            <section key={sec.title}>
              <h3>{sec.title}</h3>
              <ul>{sec.lines.map((l) => <li key={l}>{l}</li>)}</ul>
            </section>
          ))}
        </div>
        {def.tutorial && (
          <a className="gx-sp-rules-video" href={def.tutorial.url} target="_blank" rel="noopener noreferrer">
            ▶ Watch a video tutorial <small>{def.tutorial.title} (YouTube)</small>
          </a>
        )}
      </div>
    </div>,
    host,
  );
}

export function HelpButton({ def, onHelp, compact = false }: { def: GameClientDef; onHelp: () => void; compact?: boolean }) {
  if (!def.rules?.length) return null;
  return compact
    ? <button className="gx-sp-btn gx-sp-btn--ghost gx-sp-help" onClick={onHelp} aria-label={`How to play ${def.label}`} title={`How to play ${def.label}`}>?</button>
    : <button className="gx-sp-link gx-sp-howto" onClick={onHelp}>How to play {def.label}</button>;
}

export function LeaderboardButton({ onLeaderboard, compact = false }: { onLeaderboard: () => void; compact?: boolean }) {
  return compact
    ? <button className="gx-sp-btn gx-sp-btn--ghost gx-sp-help" onClick={onLeaderboard} aria-label="Leaderboard" title="Leaderboard">🏆</button>
    : <button className="gx-sp-link gx-sp-howto" onClick={onLeaderboard}>Leaderboard</button>;
}

export function PanelError({ entry }: { entry: TableEntry }) {
  return entry.error ? <span className="gx-sp-error">{entry.error}</span> : null;
}

/** Seconds left until `deadline` (ms epoch), ticking; null when there's none. */
export function useCountdown(deadline: number | null | undefined): number | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (!deadline) return;
    const t = window.setInterval(() => force((n) => n + 1), 250);
    return () => window.clearInterval(t);
  }, [deadline]);
  return deadline ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000)) : null;
}

/** "0:23" countdown chip (turn timeout or a game clock). Hidden when there's no deadline. */
export function TurnTimer({ deadline, label, warnAt = 10 }: { deadline: number | null | undefined; label?: string; warnAt?: number }) {
  const left = useCountdown(deadline);
  if (left === null) return null;
  const txt = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
  return <span className={`gx-sp-timer ${left <= warnAt ? 'is-low' : ''}`} role="timer" aria-live="off">{label ? `${label} ` : ''}{txt}</span>;
}

/** The not-seated prompt: "♠ Spades · 2/4 seated · [Play Spades] · How to play · Leaderboard". */
export function JoinPrompt({ def, pid, entry, table, me, onHelp, onLeaderboard }: OverlayProps) {
  const st = table.state;
  const join = joinOption(def, st);
  const canHost = !!def.host && def.host !== 'none' && !st.host && st.phase !== 'over';
  return (
    <div className="gx-sp gx-sp--prompt" role="region" aria-label={`${def.label} table`}>
      <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> {def.label}</span>
      <span className="gx-sp-note">
        {st.phase === 'lobby' ? `${seatedCount(st)}/${def.maxPlayers > 8 ? `${def.maxPlayers} max` : def.maxPlayers} ${def.maxPlayers > 8 ? 'joined' : 'seated'}`
          : st.phase === 'over' ? 'Game finished' : 'Game in progress'}
        {st.host ? ` · hosted by ${st.host.name}` : ''}
      </span>
      {!me ? <span className="gx-sp-note">Sign in to play</span>
        : join ? (
          <button className="gx-sp-btn gx-sp-btn--primary" disabled={entry.busy} onClick={() => void joinTable(pid, st)}>{join.label}</button>
        ) : <span className="gx-sp-note">Watching</span>}
      {me && canHost && <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void tableAction(pid, 'host')}>Host</button>}
      <HelpButton def={def} onHelp={onHelp} />
      <LeaderboardButton onLeaderboard={onLeaderboard} />
      <PanelError entry={entry} />
    </div>
  );
}

/** Seat rows for the lobby. Big tables (> 8 seats) list only who's in. */
export function SeatList({ def, pid, entry, table, seat, seatLabel }: Pick<OverlayProps, 'def' | 'pid' | 'entry' | 'table' | 'seat'> & {
  /** Small caption over each seat (default: its seat name). */
  seatLabel?: (i: number) => string;
}) {
  const st = table.state;
  const big = st.seats.length > 8;
  const rows = st.seats.map((s, i) => ({ s, i })).filter(({ s }) => !big || s);
  const open = st.seats.length - seatedCount(st);
  return (
    <div className="gx-sp-seats">
      {st.host && (
        <div className="gx-sp-seat gx-sp-seat--host">
          <span className="gx-sp-seat-pos">Host</span>
          <span className="gx-sp-seat-name">{st.host.name}</span>
        </div>
      )}
      {rows.map(({ s, i }) => (
        <div key={i} className={`gx-sp-seat ${i === seat ? 'is-me' : ''}`}>
          {!big && <span className="gx-sp-seat-pos">{seatLabel ? seatLabel(i) : seatLabelOf(def, i)}</span>}
          {s ? (
            <span className="gx-sp-seat-name">
              {s.name}{s.kind === 'bot' ? ' (computer)' : ''}{i === seat ? ' (you)' : ''}
              {s.kind === 'bot' && <button className="gx-sp-link" onClick={() => void tableAction(pid, 'remove_bot', { seat: i })}>remove</button>}
            </span>
          ) : def.bots ? (
            <button className="gx-sp-link" disabled={entry.busy} onClick={() => void tableAction(pid, 'add_bot', { seat: i })}>+ computer player</button>
          ) : <span className="gx-sp-note">Open</span>}
        </div>
      ))}
      {big && open > 0 && <div className="gx-sp-seat"><span className="gx-sp-note">{open} more can join</span></div>}
    </div>
  );
}

/** Lobby buttons: fill with computer players, host, start. */
export function LobbyActions({ def, pid, entry, table, me }: Pick<OverlayProps, 'def' | 'pid' | 'entry' | 'table' | 'me'>) {
  const st = table.state;
  const n = seatedCount(st);
  const exact = def.minPlayers === def.maxPlayers;
  const needHost = def.host === 'required' && !st.host;
  const ready = n >= def.minPlayers && !needHost;
  const full = st.seats.every(Boolean);
  return (
    <div className="gx-sp-actions">
      {def.bots && !full && <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void tableAction(pid, 'fill_bots')}>Fill with computer players</button>}
      {def.host && def.host !== 'none' && !st.host && me && <button className="gx-sp-btn" disabled={entry.busy} onClick={() => void tableAction(pid, 'host')}>Host</button>}
      <button className="gx-sp-btn gx-sp-btn--primary" disabled={!ready || entry.busy} onClick={() => void tableAction(pid, 'start')}>
        {ready ? 'Start game' : needHost && n >= def.minPlayers ? 'Waiting for a host…' : exact ? 'Waiting for players…' : `Waiting for players (${n}/${def.minPlayers})…`}
      </button>
    </div>
  );
}

/** The whole lobby panel for a seated player / the host. */
export function LobbyPanel(props: OverlayProps & { seatLabel?: (i: number) => string; footer?: React.ReactNode }) {
  const { def, pid, entry, onHelp, footer, worldId } = props;
  const [board, setBoard] = useState(false);
  return (
    <div className="gx-sp gx-sp--panel" role="region" aria-label={`${def.label} table`}>
      <div className="gx-sp-head">
        <span className="gx-sp-prompt-title"><span className="gx-sp-spade">{def.glyph}</span> {def.label}</span>
        <div className="gx-sp-head-actions">
          <button className={`gx-sp-btn gx-sp-btn--ghost ${board ? 'is-on' : ''}`} onClick={() => setBoard((v) => !v)} aria-pressed={board}>Leaderboard</button>
          <button className="gx-sp-btn gx-sp-btn--ghost" onClick={() => leaveTable(pid)}>Leave</button>
        </div>
      </div>
      {board ? <Leaderboard kind={def.kind} worldId={worldId} compact title={false} limit={8} /> : <SeatList {...props} />}
      <LobbyActions {...props} />
      <p className="gx-sp-note">{footer} <HelpButton def={def} onHelp={onHelp} /></p>
      <PanelError entry={entry} />
    </div>
  );
}

/** Game over: the final line, Rematch, and this world's leaderboard (refreshed for this game). */
export function GameOverBlock({ def, pid, entry, table, worldId, summary }: Pick<OverlayProps, 'def' | 'pid' | 'entry' | 'table' | 'worldId'> & { summary: React.ReactNode }) {
  return (
    <>
      <div className="gx-sp-actions">
        <span className="gx-sp-final">{summary}</span>
        <button className="gx-sp-btn gx-sp-btn--primary" disabled={entry.busy} onClick={() => void tableAction(pid, 'rematch')}>Rematch</button>
      </div>
      <Leaderboard kind={def.kind} worldId={worldId} compact limit={5} refreshKey={table.state.gameId} />
    </>
  );
}
